import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import HomePage from './components/HomePage';
import Header from './components/Header';
import Footer from './components/Footer';
import GlobalStyle from './components/GlobalStyle';
import RegisterUser from './components/registeruser';
import ScrollToTopButton from './components/ScrollToTopButton';
import PerfumeDetail from './components/PerfumeDetail';
import GiftSetDetail from './components/GiftSetDetail';
import GiftSetsPage from './components/GiftSetsPage';
import CartOverlay from './components/CartOverlay';
import CategoryPage from './components/CategoryPage';
import RegisterUserMobile from './components/registerusermobile';
import styled from 'styled-components';
import { auth } from "./components/firebase";
import { onAuthStateChanged } from "firebase/auth";
import BrandsPage from "./components/BrandsPage";
import BrandPage from "./components/BrandPage";
import ResetPassword from "./components/ResetPassword";
import ChangePassword from "./components/ChangePassword";
import RegisterUserProfile from "./components/RegisterUserProfile";
import MyPurchases from "./components/MyPurchases";
import ProtectedRoutes from "./components/ProtectedRoutes";
import ScrollToTop from "./components/ScrollToTop";
import { useLanguage } from "./components/LanguageContext";
import { FavoritesProvider } from "./components/FavoritesContext";
import FavoritesPage from "./components/FavoritesPage";
import CompleteRegistration from "./components/CompleteRegistration";
import VerifySuccess from "./components/VerifySuccess";
import VerifyFailed from "./components/VerifyFailed";
import PaymentSuccess from "./components/PaymentSuccess";
import AboutPage from "./components/AboutPage";
import CollectionPage from "./components/CollectionPage";
import CompleteProfile from "./components/complete-profile";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsPage from "./components/TermsPage";
import CollectionsDirectory from "./components/CollectionsDirectory";
import CookiesPolicy from "./components/CookiesPolicy";
import RegistrationSuccess from "./components/RegistrationSuccess";
import { DataRefreshProvider } from "./components/liveUpdates/DataRefreshProvider";
import PaymentFailed from "./components/PaymentFailed";
import GoogleAuthToast from "./components/GoogleAuthToast";
import axios from "axios";

axios.defaults.withCredentials = true;
axios.defaults.headers.common["Cache-Control"] = "no-store, no-cache, must-revalidate";
axios.defaults.headers.common["Pragma"] = "no-cache";
axios.defaults.headers.common["Expires"] = "0";

const API_BASE_URL = "https://shopaholics.ge/api";

const App = () => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [isCartOverlayOpen, setCartOverlayOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [user, setUser] = useState(null);
  const { t, language } = useLanguage();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const isSyncingRef = React.useRef(false);

  // ერთიანი qty-მომპოვებელი
const getQty = (obj) => Number(obj?.quantity ?? obj?.qty ?? obj?.count ?? obj?.amount ?? 0);
function extractPerfumeAdditions(p) {
  // A) canonical array: selected_ml: [{ size, quantity|qty|count|amount }]
  const arr = normalizeSelectedMl(p?.selected_ml);
  if (arr.length) return arr;

  // B) object map: { "50": 2, "100": 1 } ან { "50ml": 2 }
  const maps = p && typeof p === "object"
    ? (p.mlQuantities || p.qtyBySize || p.quantities || p.selectedQuantities || p.sizesMap)
    : null;
  if (maps && typeof maps === "object") {
    const out = [];
    for (const [k,v] of Object.entries(maps)) {
      const sz = Number(String(k).replace(/[^\d.]/g, "")); // "50ml" -> 50
      const q  = Number(v);
      if (sz > 0 && q > 0) out.push({ size: sz, quantity: q });
    }
    if (out.length) return out;
  }

  // C) mixed arrays: sizes: [{ size, qty }], mlList: [{ ml, count }]
  const arrays = p?.sizes || p?.sizeList || p?.mlList || p?.selectedSizes || p?.selectedRows;
  if (Array.isArray(arrays)) {
    const out = normalizeSelectedMl(arrays);
    if (out.length) return out;
  }

  // D) single: mlSize/ml/selectedSize + quantity/qty/...
  const singleSize = getSingleSize(p);
  const singleQty  = getQty(p);
  if (singleSize != null && Number(singleQty) > 0) {
    return [{ size: Number(singleSize), quantity: Number(singleQty) }];
  }

  return [];
}

// ერთიანი size-მომპოვებელი (ერთელადი არჩევებისთვის)
const getSingleSize = (p) =>
  (p?.mlSize ?? p?.size ?? p?.selectedSize ?? p?.ml ?? null);

// selected_ml normalizer: ითვალისწინებს {size, quantity} ან {size, qty} ან {ml, count} სტრუქტურებს
const normalizeSelectedMl = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map(r => ({
    size: Number(r?.size ?? r?.ml ?? r?.selectedSize ?? r?.size_label ?? r?.sizeValue ?? 0),
    quantity: getQty(r),
  })).filter(x => x.size > 0 && x.quantity > 0);
};

  /* ================= Auth watcher ================= */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
    });
    return () => unsubscribe();
  }, []);

  /* ================= Cart sync for logged-in ================= */
  const syncCartForLoggedInUser = React.useCallback(async () => {
    if (isSyncingRef.current) return;
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return;

    const u = JSON.parse(storedUser);
    if (!u?.id) return;

    isSyncingRef.current = true;
    try {
      const storedCart = localStorage.getItem("cart");
      if (storedCart) {
        const localCart = JSON.parse(storedCart || "[]");
        if (Array.isArray(localCart) && localCart.length) {
          const { data } = await axios.post(`${API_BASE_URL}/merge_cart.php?t=${Date.now()}`, {
            user_id: u.id,
            cart: localCart,
          });
          if (data?.success && Array.isArray(data.cart)) {
            setCart(data.cart);
            localStorage.setItem("cart", JSON.stringify(data.cart));
          }
          return;
        }
      }
      const { data } = await axios.get(`${API_BASE_URL}/get_cart.php?user_id=${u.id}&t=${Date.now()}`);
      if (data?.success && Array.isArray(data.cart)) {
        setCart(data.cart);
        localStorage.setItem("cart", JSON.stringify(data.cart));
      }
    } catch (e) {
      console.error("syncCart error", e);
    } finally {
      isSyncingRef.current = false;
    }
  }, []);

  /* ================= Modal UI ================= */
  const ModalWrapper = styled.div`
    position: fixed; inset: 0; display:flex; justify-content:center; align-items:center;
    background-color: rgba(0,0,0,.5); z-index: 1000;
  `;
  const ModalContent = styled.div`
    background:#fff; padding:20px; border-radius:8px; width:300px; text-align:center;
    box-shadow:0 4px 10px rgba(0,0,0,.2);
  `;
  const ModalHeader = styled.h2` font-size:1rem; margin-bottom:10px; `;
  const ModalButton = styled.button`
    margin-top:15px; padding:10px 20px; background:rgb(0,0,0); color:#fff; border:none;
    border-radius:4px; cursor:pointer; font-size:1rem;
    &:hover { background: rgb(59,59,59); }
  `;
  const Modal = ({ message, onClose }) => (
    <ModalWrapper>
      <ModalContent>
        <ModalHeader>{message}</ModalHeader>
        <ModalButton onClick={onClose}>{t("brands.close")}</ModalButton>
      </ModalContent>
    </ModalWrapper>
  );

  useEffect(() => {
    try { localStorage.setItem('cart', JSON.stringify(cart)); } catch {}
  }, [cart]);

  /* =========================================================
     PERFUME ML strategy helpers (full + units mixed support)
     ========================================================= */
  function mlStrategy(ml, item) {
    if (ml?.stock_strategy === "units") return "units";
    if (ml?.stock_strategy === "full")  return "full";
    // fallback to legacy flag:
    return item?.per_ml_stock && ml?.unit_stock != null ? "units" : "full";
  }

  function pickDefaultMlForQuickAdd(item) {
    const mls = Array.isArray(item?.milliliters) ? item.milliliters : [];
    if (mls.length === 0) return null;
    // Prefer units with stock, choose cheapest
    const unitsWithStock = mls
      .map(m => ({...m, _strategy: mlStrategy(m, item)}))
      .filter(m => m._strategy === "units" && Number(m.unit_stock || 0) > 0);
    if (unitsWithStock.length > 0) {
      unitsWithStock.sort((a,b) => (Number(a.sale_price ?? a.price) - Number(b.sale_price ?? b.price)));
      return unitsWithStock[0];
    }
    // Else try full with pooled stock, choose smallest size
    const fullRows = mls.map(m => ({...m, _strategy: mlStrategy(m, item)})).filter(m => m._strategy === "full");
    if (fullRows.length > 0 && Number(item.full_stock || 0) > 0) {
      fullRows.sort((a,b) => Number(a.size||0) - Number(b.size||0));
      return fullRows[0];
    }
    // Fallback first
    return {...mls[0], _strategy: mlStrategy(mls[0], item)};
  }

  // merge selected_ml additions into existing perfume item with caps
  function mergePerfumeSelectedMl(lineItem, additions /* array of {size, quantity} */) {
    const item = {...lineItem};
    const mls = Array.isArray(item.milliliters) ? item.milliliters : [];
    const selected = Array.isArray(item.selected_ml) ? [...item.selected_ml] : [];

    const bySize = new Map(selected.map(r => [String(r.size), Number(r.quantity || 0)]));

    // helper to get ML row by size
    const getMl = (size) => mls.find(m => Number(m.size) === Number(size));

    // First, compute how much pooled ML already used by full-strategy rows (before adding)
    const usedFullMl = () => {
      return Array.from(bySize.entries()).reduce((s,[k,q]) => {
        const rowMl = getMl(k);
        if (!rowMl) return s;
        if (mlStrategy(rowMl, item) !== "full") return s;
        return s + Number(q) * Number(rowMl.size || 0);
      }, 0);
    };

    for (const add of additions) {
      const sz = Number(add.size);
      const want = Number(add.quantity || 0);
      if (!want || isNaN(want) || want <= 0) continue;

      const ml = getMl(sz);
      if (!ml) continue;

      const strategy = mlStrategy(ml, item);
      const currentQty = bySize.get(String(sz)) || 0;

      if (strategy === "units") {
        const cap = Number(ml.unit_stock || 0);
        const next = Math.min(currentQty + want, cap);
        bySize.set(String(sz), next);
      } else {
        // full pooled
        const fullCap = Number(item.full_stock || 0);
        const othersUsedMl = usedFullMl() - (currentQty * Number(ml.size || 0)); // exclude this size current
        const remainingMl = Math.max(0, fullCap - othersUsedMl);
        const maxForThis = Math.floor(remainingMl / Number(ml.size || 1));
        const next = Math.min(currentQty + want, maxForThis);
        bySize.set(String(sz), next);
      }
    }

    const nextSelected = mls.map(ml => {
      const q = bySize.get(String(ml.size)) || 0;
      return q > 0 ? { size: Number(ml.size), quantity: Number(q) } : null;
    }).filter(Boolean);

    const totalUnits = nextSelected.reduce((s,r) => s + Number(r.quantity || 0), 0);
    item.selected_ml = nextSelected;
    item.quantity = Math.max(1, totalUnits || 1);
    return item;
  }

  /* =========================================================
     ACCESSORY helpers (variant resolution + price normalize)
     ========================================================= */
  function resolveVariant(product) {
    const vid =
      product.accessory_variant_id ??
      product.selectedVariant?.id ??
      product.selectedVariantId ??
      null;

    const arr =
      (Array.isArray(product.variants) && product.variants) ||
      (Array.isArray(product.accessory_variants) && product.accessory_variants) ||
      [];

    if (vid != null) {
      const found = arr.find(v => String(v.id) === String(vid));
      if (found) return found;
    }
    if (product.selectedVariant && product.selectedVariant.id != null) return product.selectedVariant;
    return arr[0] ?? null;
  }

  function variantPriceFields(variant, product) {
    const vPrice = Number(variant?.price ?? product?.price_p ?? product?.price ?? 0);
    const vSaleRaw = variant?.sale_p ?? product?.sale_p ?? null;
    const vSale = (vSaleRaw != null) ? Number(vSaleRaw) : null;
    const useSale = vSale != null && vSale > 0 && vSale < vPrice;
    return { price_p: vPrice, sale_p: useSale ? vSale : null };
  }

  /* ======================== addToCart (detail-aware) ======================== */
  const addToCart = async (product) => {
    if (!product) return;

    const storedUser = localStorage.getItem("user");
    const userObj = storedUser ? JSON.parse(storedUser) : null;
    const isLoggedIn = !!userObj?.id;

    // 1) GIFT SETS (respect qty from detail)
    if (product.type === "gift_set") {
      const productId = String(product.gift_set_id ?? product.id ?? product.slug ?? Date.now());
      const qtyToAdd = Math.max(1, getQty(product) || 1);
      const unitPrice = Number(product.unit_price ?? product.final_price ?? 0);
      const fullStock = Number(product.full_stock ?? 999);
      const cover = product.cover || product.image || (product.images?.[0]) || "";
      const title = product.title || product.name || "";

      setCart(prev => {
        const next = [...prev];
        const idx = next.findIndex(it => it.type === "gift_set" && String(it.product_id ?? it.id) === productId);
        if (idx >= 0) {
          const line = { ...next[idx] };
          const cap = Number(line.full_stock ?? fullStock ?? 999);
          line.quantity = Math.min((line.quantity || 1) + qtyToAdd, cap);
          line.unit_price = unitPrice;
          line.image = cover;
          next[idx] = line;
        } else {
          next.push({
            type: "gift_set",
            id: productId,
            product_id: productId,
            name: title,
            title,
            unit_price: unitPrice,
            quantity: Math.min(qtyToAdd, fullStock),
            full_stock: fullStock,
            image: cover,
          });
        }
        try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
        return next;
      });

      if (isLoggedIn) {
        try {
          const fresh = JSON.parse(localStorage.getItem("cart") || "[]");
          const { data } = await axios.post(`${API_BASE_URL}/merge_cart.php?t=${Date.now()}`, {
            user_id: userObj.id,
            cart: fresh,
          });
          if (data?.success && Array.isArray(data.cart)) {
            setCart(data.cart);
            localStorage.setItem("cart", JSON.stringify(data.cart));
          }
        } catch (err) { console.error("merge failed (gift_set)", err); }
      }
      return;
    }

    // 2) Other types
    const productId = (product.product_id ?? product.id ?? product.pid)?.toString();
    if (!productId) return;

    const isPerfumeType = ["perfume", "skin-care", "hair-and-body-care", "atomizers"].includes(product.category);
    const type = isPerfumeType ? "perfume" : "accessory";


    setCart(prev => {
      let next = [...prev];

      // ACCESSORY — respect chosen variant + qty from detail
      if (type === "accessory") {
        const variant = resolveVariant(product);
        const variantId = product.accessory_variant_id ?? variant?.id ?? product.selectedVariantId ?? null;
        const qtyToAdd = Math.max(1, getQty(product) || 1);

        const variantImage =
          (Array.isArray(variant?.images) && variant.images[0]) ||
          product.image ||
          (Array.isArray(product.imageUrls) && product.imageUrls[0]) ||
          "";

        const { price_p, sale_p } = variantPriceFields(variant, product);
        const variantStock = Number(
          variant?.stock ??
          product?.selectedVariant?.stock ??
          product?.full_stock ?? 0
        );

        const idx = next.findIndex(it =>
          it.type === "accessory" &&
          String(it.product_id) === String(productId) &&
          String(it.accessory_variant_id ?? "") === String(variantId ?? "")
        );

        if (idx >= 0) {
          const line = { ...next[idx] };
          const cap = Number(line.full_stock ?? variantStock ?? 999);
          line.quantity = Math.min((line.quantity || 1) + qtyToAdd, cap);
          // keep latest fields
          line.price_p = price_p;
          line.sale_p  = sale_p;
          line.image   = variantImage;
          line.full_stock = cap;
          line.selectedVariant = {
            id: variantId,
            color_ka: variant?.color_ka ?? product?.color_ka ?? "",
            color_en: variant?.color_en ?? product?.color_en ?? "",
            size_label: variant?.size_label ?? product?.size_label ?? "",
            stock: cap,
            price: price_p,
            sale_p: sale_p,
            images: Array.isArray(variant?.images) ? variant.images : [],
          };
          next[idx] = line;
        } else {
          next.push({
            ...product,
            id: productId,
            product_id: productId,
            type: "accessory",
            quantity: Math.min(qtyToAdd, variantStock),
            full_stock: variantStock,
            image: variantImage,
            accessory_variant_id: variantId,
            price_p,
            sale_p,
            selectedVariant: {
              id: variantId,
              color_ka: variant?.color_ka ?? product?.color_ka ?? "",
              color_en: variant?.color_en ?? product?.color_en ?? "",
              size_label: variant?.size_label ?? product?.size_label ?? "",
              stock: variantStock,
              price: price_p,
              sale_p: sale_p,
              images: Array.isArray(variant?.images) ? variant.images : [],
            },
          });
        }

        try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
        return next;
      }

      // PERFUME — read selection from detail if provided
      // PERFUME — read selection from detail if provided
// PERFUME — read selection from detail if provided
const idx = next.findIndex(it =>
  (it.type === "perfume" || it.category === "perfume") &&
  String(it.product_id) === String(productId)
);

// ამოიღე დეტაილიდან ყველა შესაძლო ფორმატით
const additions = extractPerfumeAdditions(product);

if (idx >= 0) {
  let line = { ...next[idx] };

  if (additions.length) {
    line = mergePerfumeSelectedMl(
      line,
      additions.map(r => ({ size: Number(r.size), quantity: Number(r.quantity) }))
    );
  } else {
    // default quick add (+1 of best ML)
    line = mergePerfumeSelectedMl(line, (() => {
      const picked = pickDefaultMlForQuickAdd(line);
      if (!picked) return [];
      return [{ size: Number(picked.size), quantity: 1 }];
    })());
  }

  next[idx] = line;
} else {
  const base = {
    ...product,
    id: productId,
    product_id: productId,
    type: "perfume",
    image: product.imageUrls?.[0] || product.image || "",
    full_stock: Number(product.full_stock ?? 0),
    quantity: 1,
    selected_ml: [],
  };

  if (additions.length) {
    const hydrated = mergePerfumeSelectedMl(
      base,
      additions.map(r => ({ size: Number(r.size), quantity: Number(r.quantity) }))
    );
    next.push(hydrated);
  } else {
    const ml = pickDefaultMlForQuickAdd(base);
    if (ml) base.selected_ml = [{ size: Number(ml.size), quantity: 1 }];
    const totalUnits = base.selected_ml.reduce((s,r) => s + Number(r.quantity || 0), 0);
    base.quantity = Math.max(1, totalUnits || 1);
    next.push(base);
  }
}


      try { localStorage.setItem("cart", JSON.stringify(next)); } catch {}
      return next;
    });

    if (isLoggedIn) {
      try {
        const fresh = JSON.parse(localStorage.getItem("cart") || "[]");
        const { data } = await axios.post(`${API_BASE_URL}/merge_cart.php?t=${Date.now()}`, {
          user_id: userObj.id,
          cart: fresh,
        });
        if (data?.success && Array.isArray(data.cart)) {
          setCart(data.cart);
          localStorage.setItem("cart", JSON.stringify(data.cart));
        }
      } catch (err) {
        console.error("merge failed", err);
      }
    }
  };

  /* ================= Quantity buttons (simple caps) ================= */
  const incrementQuantity = (index) => {
    setCart(prevCart => {
      const newCart = [...prevCart];
      if ((newCart[index].quantity || 1) < (newCart[index].full_stock || 0)) {
        newCart[index].quantity = (newCart[index].quantity || 1) + 1;
      } else {
        setModalMessage(
          language === "en"
            ? "Maximum stock limit reached for this item."
            : "ამ ნივთის მაქსიმალური რაოდენობა მიღწეულია."
        );
        setShowModal(true);
      }
      try { localStorage.setItem("cart", JSON.stringify(newCart)); } catch {}
      return newCart;
    });
  };

  const decrementQuantity = (index) => {
    setCart(prevCart => {
      const newCart = [...prevCart];
      if ((newCart[index].quantity || 1) > 1) {
        newCart[index].quantity = (newCart[index].quantity || 1) - 1;
      } else {
        newCart.splice(index, 1);
      }
      try { localStorage.setItem("cart", JSON.stringify(newCart)); } catch {}
      return newCart;
    });
  };

  const toggleCartOverlay = () => setCartOverlayOpen(prev => !prev);

  const deleteItemFromCart = async (index) => {
    const itemToDelete = cart[index];
    if (!itemToDelete) return;

    setCart(prev => {
      const updated = prev.filter((_, i) => i !== index);
      try { localStorage.setItem("cart", JSON.stringify(updated)); } catch {}
      return updated;
    });

    setSelectedOptions(prev => {
      const updated = prev.filter((_, i) => i !== index);
      try { localStorage.setItem("selectedOptions", JSON.stringify(updated)); } catch {}
      return updated;
    });

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const u = JSON.parse(storedUser);
        await axios.post(`${API_BASE_URL}/delete_cart_item.php?t=${Date.now()}`, {
          user_id: u.id,
          product_id: itemToDelete.product_id ?? itemToDelete.id,
          type: itemToDelete.type,
          ml: itemToDelete.ml,
        });
      } catch (err) {
        console.error("server delete error", err);
      }
    }
  };

  /* ================= storage + first syncs ================= */
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "user") {
        const newVal = e.newValue ? JSON.parse(e.newValue) : null;
        setUser(newVal);
        if (newVal?.id) syncCartForLoggedInUser();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [syncCartForLoggedInUser]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const localOptions = localStorage.getItem("selectedOptions");
    if (storedUser && localOptions) {
      const u = JSON.parse(storedUser);
      const parsedOptions = JSON.parse(localOptions);
      if (u?.id && parsedOptions?.length > 0) {
        axios
          .post(`${API_BASE_URL}/save_selected_options.php`, {
            user_id: u.id,
            selected_options: parsedOptions,
          })
          .catch((err) => {
            console.error("❌ Failed to sync selectedOptions:", err);
          });
      }
    }
  }, []);

useEffect(() => {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) return;
  const u = JSON.parse(storedUser);
  if (!u?.id) return;

  (async () => {
    try {
      const storedCart = localStorage.getItem("cart");
      const localCart = storedCart ? JSON.parse(storedCart) : [];

      if (Array.isArray(localCart) && localCart.length > 0) {
        // გვაქვს ლოკალური آیტემები → შევერმაჯოთ სერვერზე
        const { data } = await axios.post(`${API_BASE_URL}/merge_cart.php?t=${Date.now()}`, {
          user_id: u.id,
          cart: localCart,
        });
        if (data?.success && Array.isArray(data.cart)) {
          setCart(data.cart);
          localStorage.setItem("cart", JSON.stringify(data.cart));
          return; // წარმატებით შევაერთეთ — აქ ვჩერდებით
        }
      }

      // ლოკალური კალათა ცარიელია ან მერჯმა ვერ დაგვიბრუნა კალათა → მოვიტანოთ სერვერიდან
      const { data } = await axios.get(`${API_BASE_URL}/get_cart.php?user_id=${u.id}&t=${Date.now()}`);
      if (data?.success && Array.isArray(data.cart)) {
        setCart(data.cart);
        localStorage.setItem("cart", JSON.stringify(data.cart));
      }
    } catch (e) {
      console.error("cart sync error", e);
    }
  })();
}, []);

useEffect(() => {
  const unsub = onAuthStateChanged(auth, (authUser) => {
    setUser(authUser);
    if (authUser) { syncCartForLoggedInUser(); }
  });
  return () => unsub();
}, [syncCartForLoggedInUser]);

  useEffect(() => {
    function onPageShow(e) {
      if (e.persisted) {
        try {
          const fresh = JSON.parse(localStorage.getItem("cart") || "[]");
          setCart(fresh);
        } catch {
          setCart([]);
        }
      }
    }
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  /* ================= Render ================= */
  return (
    <>
      <FavoritesProvider>
        <Router>
          <GlobalStyle />
          <DataRefreshProvider apiBase="https://shopaholics.ge/api">
            <Header toggleCartOverlay={toggleCartOverlay} cart={cart} user={user} />
            <ProtectedRoutes setUser={setUser} />
            <ScrollToTop />
            <GoogleAuthToast />
            <Routes>
              <Route path="/" element={<HomePage addToCart={addToCart} />} />
              <Route path="/favorites" element={<FavoritesPage addToCart={addToCart} />} />
              <Route path="/registeruser" element={<RegisterUser />} />
              <Route path="/login-mobile" element={<RegisterUserMobile />} />
              <Route path="/product/:product_id" element={<PerfumeDetail addToCart={addToCart} />} />
              <Route path="/gift-set/:gift_set_id" element={<GiftSetDetail addToCart={addToCart} />} />
              <Route path="/gift-sets" element={<GiftSetsPage addToCart={addToCart} />} />
              <Route path="/products" element={<CategoryPage addToCart={addToCart} />} />
              <Route path="/best-sellers" element={<CategoryPage addToCart={addToCart} />} />
              <Route path="/BrandsPage" element={<BrandsPage />} />
              <Route path="/brands/:brand" element={<BrandPage addToCart={addToCart} />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/login" element={<RegisterUserProfile />} />
              <Route path="/my-purchases" element={<MyPurchases />} />
              <Route path="/complete-registration" element={<CompleteRegistration />} />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="/verify-success" element={<VerifySuccess />} />
              <Route path="/verify-failed" element={<VerifyFailed />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/about-us" element={<AboutPage />} />
              <Route path="/collections/:slugOrId" element={<CollectionPage addToCart={addToCart} />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/collections" element={<CollectionsDirectory />} />
              <Route path="/cookies" element={<CookiesPolicy />} />
              <Route path="/registration-success" element={<RegistrationSuccess />} />
              <Route path="/payment-failed" element={<PaymentFailed />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>

            <CartOverlay
              setCart={setCart}
              cart={cart}
              placeOrder={() => {}}
              isOpen={isCartOverlayOpen}
              toggleCartOverlay={toggleCartOverlay}
              deleteItemFromCart={deleteItemFromCart}
            />

            <ScrollToTopButton />
            <Footer />
          </DataRefreshProvider>
        </Router>

        {showModal && (
          <Modal
            message={modalMessage}
            onClose={() => setShowModal(false)}
          />
        )}
      </FavoritesProvider>
    </>
  );
};

export default App;
